import { useState, useEffect } from 'react'
import { Users, UserPlus, Edit2, Trash2, Key, Check, X, MoreVertical, UserX, XCircle, UserCheck } from 'lucide-react'
import { apiService } from '../services/api'

export default function AdminUsersPage() {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [openMenuUserId, setOpenMenuUserId] = useState(null)

  useEffect(() => {
    loadUsers()
  }, [])

  const loadUsers = async () => {
    try {
      setLoading(true)
      const response = await apiService.getUsers()
      setUsers(response.data.users)
      setError(null)
    } catch (err) {
      setError('Nie udało się załadować użytkowników')
      console.error('Error loading users:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateUser = async (userData) => {
    try {
      await apiService.createUser(userData)
      await loadUsers()
      setShowCreateModal(false)
    } catch (err) {
      console.error('Error creating user:', err)
      alert('Błąd podczas tworzenia użytkownika: ' + (err.response?.data?.detail || err.message))
    }
  }

  const handleUpdateUser = async (userId, userData) => {
    try {
      await apiService.updateUser(userId, userData)
      await loadUsers()
      setEditingUser(null)
    } catch (err) {
      console.error('Error updating user:', err)
      alert('Błąd podczas aktualizacji użytkownika: ' + (err.response?.data?.detail || err.message))
    }
  }

  const handleDeactivateUser = async (userId) => {
    setOpenMenuUserId(null)
    if (!confirm('Czy na pewno chcesz dezaktywować tego użytkownika?\n\nUżytkownik będzie mógł zostać później aktywowany.')) {
      return
    }

    try {
      await apiService.deleteUser(userId)
      await loadUsers()
      alert('Użytkownik został dezaktywowany')
    } catch (err) {
      console.error('Error deactivating user:', err)
      alert('Błąd podczas dezaktywacji użytkownika: ' + (err.response?.data?.detail || err.message))
    }
  }

  const handleActivateUser = async (userId) => {
    setOpenMenuUserId(null)
    if (!confirm('Czy na pewno chcesz aktywować tego użytkownika?')) {
      return
    }

    try {
      await apiService.activateUser(userId)
      await loadUsers()
      alert('Użytkownik został aktywowany')
    } catch (err) {
      console.error('Error activating user:', err)
      alert('Błąd podczas aktywacji użytkownika: ' + (err.response?.data?.detail || err.message))
    }
  }

  const handlePermanentDeleteUser = async (userId, userEmail) => {
    setOpenMenuUserId(null)
    if (!confirm(`⚠️ UWAGA: Czy na pewno chcesz PERMANENTNIE usunąć użytkownika ${userEmail}?\n\nTA OPERACJA JEST NIEODWRACALNA!\n\nWszystkie dane użytkownika zostaną usunięte z bazy danych.`)) {
      return
    }

    // Double confirm for safety
    const confirmText = prompt('Wpisz "USUŃ" aby potwierdzić permanentne usunięcie:')
    if (confirmText !== 'USUŃ') {
      alert('Operacja anulowana')
      return
    }

    try {
      await apiService.permanentlyDeleteUser(userId)
      await loadUsers()
      alert('Użytkownik został permanentnie usunięty')
    } catch (err) {
      console.error('Error permanently deleting user:', err)
      alert('Błąd podczas usuwania użytkownika: ' + (err.response?.data?.detail || err.message))
    }
  }

  const handleResetPassword = async (userId) => {
    const newPassword = prompt('Wprowadź nowe hasło (min. 8 znaków, wielka litera, cyfra):')
    if (!newPassword) return

    try {
      await apiService.resetUserPassword(userId, newPassword)
      alert('Hasło zostało zresetowane')
    } catch (err) {
      console.error('Error resetting password:', err)
      alert('Błąd podczas resetowania hasła: ' + (err.response?.data?.detail || err.message))
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Ładowanie użytkowników...</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Zarządzanie użytkownikami</h2>
          <p className="text-gray-600 mt-1">Twórz i zarządzaj użytkownikami systemu</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Dodaj użytkownika
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Użytkownik
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rola
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Firma
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ostatnie logowanie
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Akcje
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user, index) => {
              // Menu opens upward for last user, and second-to-last if it's not the first/second user
              const isNearBottom = (index === users.length - 1) || (index === users.length - 2 && index >= 2)
              return (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Users className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">{user.name}</div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                    {user.role}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.company || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {user.is_active ? (
                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      <Check className="w-3 h-3 mr-1" />
                      Aktywny
                    </span>
                  ) : (
                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                      <X className="w-3 h-3 mr-1" />
                      Nieaktywny
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.last_login_at ? new Date(user.last_login_at).toLocaleString('pl-PL') : 'Nigdy'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end space-x-2">
                    <button
                      onClick={() => setEditingUser(user)}
                      className="p-1.5 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded"
                      title="Edytuj"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleResetPassword(user.id)}
                      className="p-1.5 text-yellow-600 hover:text-yellow-900 hover:bg-yellow-50 rounded"
                      title="Resetuj hasło"
                    >
                      <Key className="w-4 h-4" />
                    </button>
                    
                    {/* Delete dropdown menu */}
                    <div className="relative">
                      <button
                        onClick={() => setOpenMenuUserId(openMenuUserId === user.id ? null : user.id)}
                        className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
                        title="Więcej opcji"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      
                      {openMenuUserId === user.id && (
                        <>
                          <div 
                            className="fixed inset-0 z-10" 
                            onClick={() => setOpenMenuUserId(null)}
                          />
                          <div className={`absolute right-0 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20 ${
                            isNearBottom ? 'bottom-full mb-1' : 'top-full mt-1'
                          }`}>
                            <div className="py-1">
                              {user.is_active ? (
                                <button
                                  onClick={() => handleDeactivateUser(user.id)}
                                  className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-orange-600 hover:bg-orange-50"
                                >
                                  <UserX className="w-4 h-4" />
                                  <span>Dezaktywuj</span>
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleActivateUser(user.id)}
                                  className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-green-600 hover:bg-green-50"
                                >
                                  <UserCheck className="w-4 h-4" />
                                  <span>Aktywuj</span>
                                </button>
                              )}
                              <button
                                onClick={() => handlePermanentDeleteUser(user.id, user.email)}
                                className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                              >
                                <XCircle className="w-4 h-4" />
                                <span>Usuń permanentnie</span>
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Create/Edit User Modal */}
      {(showCreateModal || editingUser) && (
        <UserModal
          user={editingUser}
          onClose={() => {
            setShowCreateModal(false)
            setEditingUser(null)
          }}
          onSave={editingUser ? 
            (data) => handleUpdateUser(editingUser.id, data) : 
            handleCreateUser
          }
        />
      )}
    </div>
  )
}

function UserModal({ user, onClose, onSave }) {
  const [formData, setFormData] = useState({
    email: user?.email || '',
    password: '',
    name: user?.name || '',
    role: user?.role || 'VIEWER',
    company: user?.company || '',
    notes: user?.notes || ''
  })
  
  // Available operators
  const operators = [
    { code: 'ORANGE', label: 'Orange' },
    { code: 'PLUS', label: 'Plus' },
    { code: 'TMOBILE', label: 'T-Mobile' },
    { code: 'P4', label: 'Play' }
  ]
  
  // Available features
  const features = [
    { code: 'AI_ANALYSIS', label: 'Analiza AI', description: 'Dostęp do modułu analiz AI' }
  ]
  
  // Initialize operator and feature selections
  const [selectedOperators, setSelectedOperators] = useState(
    user?.allowed_operators || []
  )
  
  const [selectedFeatures, setSelectedFeatures] = useState(
    user?.allowed_features || []
  )
  
  const [allOperators, setAllOperators] = useState(
    !user?.allowed_operators || user.allowed_operators.length === 0
  )

  const handleSubmit = (e) => {
    e.preventDefault()
    
    const data = {
      ...formData,
      // If "all operators" is checked, send null; otherwise send selected list
      allowed_operators: allOperators ? null : (selectedOperators.length > 0 ? selectedOperators : null),
      // Send selected features (empty array means no additional features)
      allowed_features: selectedFeatures.length > 0 ? selectedFeatures : null
    }
    
    // Remove password if empty (for edit mode)
    if (!data.password && user) {
      delete data.password
    }
    
    // Remove email for edit mode (can't change email)
    if (user) {
      delete data.email
    }
    
    onSave(data)
  }
  
  const handleOperatorToggle = (operatorCode) => {
    setSelectedOperators(prev => {
      if (prev.includes(operatorCode)) {
        return prev.filter(op => op !== operatorCode)
      } else {
        return [...prev, operatorCode]
      }
    })
  }
  
  const handleFeatureToggle = (featureCode) => {
    setSelectedFeatures(prev => {
      if (prev.includes(featureCode)) {
        return prev.filter(f => f !== featureCode)
      } else {
        return [...prev, featureCode]
      }
    })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            {user ? 'Edytuj użytkownika' : 'Nowy użytkownik'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {!user && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            )}
            
            {!user && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hasło</label>
                <input
                  type="password"
                  required={!user}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={user ? 'Zostaw puste, aby nie zmieniać' : 'Min. 8 znaków'}
                />
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Imię i nazwisko</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rola</label>
              <select
                required
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="ADMIN">ADMIN - Pełny dostęp</option>
                <option value="MANAGER">MANAGER - Menedżer</option>
                <option value="KAM">KAM - Key Account Manager</option>
                <option value="VIEWER">VIEWER - Tylko podgląd</option>
                <option value="OPERATOR_EXTERNAL">OPERATOR_EXTERNAL - Użytkownik zewnętrzny</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Firma</label>
              <input
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            {/* Operator Access */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Dostęp do operatorów
              </label>
              
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={allOperators}
                    onChange={(e) => {
                      setAllOperators(e.target.checked)
                      if (e.target.checked) {
                        setSelectedOperators([])
                      }
                    }}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 font-medium">
                    Dostęp do wszystkich operatorów
                  </span>
                </label>
                
                {!allOperators && (
                  <div className="mt-3 ml-6 space-y-2">
                    {operators.map(op => (
                      <label key={op.code} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedOperators.includes(op.code)}
                          onChange={() => handleOperatorToggle(op.code)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">{op.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Feature Access */}
            <div className="border border-gray-200 rounded-lg p-4 bg-blue-50">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Dostęp do specjalnych modułów
              </label>
              
              <div className="space-y-3">
                {features.map(feature => (
                  <label key={feature.code} className="flex items-start">
                    <input
                      type="checkbox"
                      checked={selectedFeatures.includes(feature.code)}
                      onChange={() => handleFeatureToggle(feature.code)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                    />
                    <div className="ml-2">
                      <span className="text-sm text-gray-700 font-medium">{feature.label}</span>
                      <p className="text-xs text-gray-500 mt-0.5">{feature.description}</p>
                    </div>
                  </label>
                ))}
                
                <p className="text-xs text-gray-600 mt-2">
                  💡 Domyślne uprawnienia: ADMIN i MANAGER mają dostęp do wszystkich modułów
                </p>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notatki</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Anuluj
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {user ? 'Zapisz' : 'Utwórz'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

